import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { Check, AlertCircle, MapPin, Mail, Phone, Globe, Clock, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang });
      setStatus({ state: "success", msg: t.contact.form.success });
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : t.contact.form.error });
    }
  };

  return (
    <div data-testid="page-contact" className="bg-white text-[#0A0A0A]">
      {/* Hero */}
      <section className="relative pt-20 pb-12 md:pt-28 md:pb-16 overflow-hidden">
        <div className="absolute -top-20 -right-32 w-[500px] h-[500px] bg-[#EA6A1A]/6 rounded-full blur-[100px] pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-[#F5F5F4] border border-[#E9E9E7] px-3 py-1.5 rounded-full text-xs font-medium mb-6 text-[#52525B]">
              — {t.contact.eyebrow}
            </div>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.98] tracking-[-0.03em] font-medium">
              {t.contact.title.replace(".", "")}<span className="italic text-[#EA6A1A]">.</span>
            </h1>
            <p className="mt-8 text-lg text-[#52525B] max-w-2xl mx-auto leading-relaxed">
              {t.contact.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12 grid lg:grid-cols-12 gap-6 md:gap-8">
          {/* Form */}
          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-7 bg-white border border-[#E9E9E7] rounded-3xl p-8 md:p-12 card-shadow"
            data-testid="contact-form"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <Field id="name" label={t.contact.form.name} value={form.name} onChange={update("name")} required testid="contact-input-name" />
              <Field id="email" label={t.contact.form.email} type="email" value={form.email} onChange={update("email")} required testid="contact-input-email" />
              <Field id="phone" label={t.contact.form.phone} value={form.phone} onChange={update("phone")} testid="contact-input-phone" />
              <div className="flex flex-col">
                <label htmlFor="service" className="text-[13px] text-[#52525B] mb-2 font-medium">
                  {t.contact.form.service}
                </label>
                <select
                  id="service"
                  value={form.service}
                  onChange={update("service")}
                  data-testid="contact-input-service"
                  className="bg-[#FAFAFA] border border-[#E9E9E7] focus:border-[#EA6A1A] outline-none rounded-xl px-4 py-3 text-[#0A0A0A] transition-colors"
                >
                  <option value="">{t.contact.form.servicePh}</option>
                  {t.servicesList.map((s) => (
                    <option key={s.id} value={s.t}>{s.t}</option>
                  ))}
                  <option value="Steuererklärung">Steuererklärung (79.– / 99.– / 119.–)</option>
                </select>
              </div>
            </div>

            <div className="mt-6 flex flex-col">
              <label htmlFor="message" className="text-[13px] text-[#52525B] mb-2 font-medium">
                {t.contact.form.message}
              </label>
              <textarea
                id="message"
                required
                value={form.message}
                onChange={update("message")}
                rows={5}
                data-testid="contact-input-message"
                className="bg-[#FAFAFA] border border-[#E9E9E7] focus:border-[#EA6A1A] outline-none rounded-xl px-4 py-3 text-[#0A0A0A] transition-colors resize-none"
              />
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <button
                type="submit"
                disabled={status.state === "loading"}
                data-testid="contact-submit"
                className="group inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#EA6A1A] text-white px-7 py-4 rounded-full transition-all shadow-sm disabled:opacity-50"
              >
                <span className="text-sm font-medium">{status.state === "loading" ? t.contact.form.sending : t.contact.form.submit}</span>
                <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
              </button>

              {status.state === "success" && (
                <div className="flex items-center gap-2 text-[#0A0A0A] text-sm" data-testid="contact-success">
                  <div className="w-6 h-6 bg-[#22C55E]/10 rounded-full flex items-center justify-center">
                    <Check size={14} className="text-[#22C55E]" strokeWidth={2.5} />
                  </div>
                  {status.msg}
                </div>
              )}
              {status.state === "error" && (
                <div className="flex items-center gap-2 text-[#DC2626] text-sm" data-testid="contact-error">
                  <AlertCircle size={16} /> {status.msg}
                </div>
              )}
            </div>
          </motion.form>

          {/* Details */}
          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-5 space-y-4"
          >
            <div className="bg-[#0A0A0A] text-white rounded-3xl p-8 md:p-10">
              <div className="text-xs text-[#EA6A1A] uppercase tracking-wider mb-6 font-medium">
                {t.nav.contact}
              </div>
              <div className="space-y-6">
                <DetailItem icon={MapPin} label={t.contact.details.addressLabel} value={t.contact.details.address} />
                <DetailItem icon={Phone} label={t.contact.details.phoneLabel} value="+41 78 311 15 84" href="tel:+41783111584" />
                <DetailItem icon={Mail} label={t.contact.details.emailLabel} value="info@omniserve-solutions.ch" href="mailto:info@omniserve-solutions.ch" />
                <DetailItem icon={Globe} label={t.contact.details.webLabel} value="www.omniserve-solutions.ch" href="https://www.omniserve-solutions.ch" />
                <DetailItem icon={Clock} label={t.contact.details.hoursLabel} value={t.contact.details.hours} />
              </div>
            </div>

            <div className="bg-[#F7F5F0] border border-[#E9E9E7] rounded-3xl p-8 flex items-start gap-4">
              <div className="w-11 h-11 bg-[#EA6A1A] rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock className="text-white" size={20} strokeWidth={1.8} />
              </div>
              <div>
                <div className="font-medium text-[15px] mb-1">24h Rückmeldung</div>
                <div className="text-sm text-[#71717A] leading-relaxed">
                  Wir melden uns innerhalb eines Werktages persönlich bei Ihnen.
                </div>
              </div>
            </div>
          </motion.aside>
        </div>
      </section>
    </div>
  );
}

function Field({ id, label, type = "text", value, onChange, required, testid }) {
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="text-[13px] text-[#52525B] mb-2 font-medium">
        {label}
      </label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="bg-[#FAFAFA] border border-[#E9E9E7] focus:border-[#EA6A1A] outline-none rounded-xl px-4 py-3 text-[#0A0A0A] transition-colors"
      />
    </div>
  );
}

function DetailItem({ icon: Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-4 group">
      <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="text-[#EA6A1A]" size={16} strokeWidth={1.7} />
      </div>
      <div>
        <div className="text-xs text-white/50 mb-1">{label}</div>
        <div className="text-white font-medium group-hover:text-[#EA6A1A] transition-colors">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="block">{inner}</a> : inner;
}
